import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MessageService} from 'primeng/api';
import {PasswordModule} from 'primeng/password';
import {ButtonModule} from 'primeng/button';
import {InputTextModule} from 'primeng/inputtext';
import {CommonModule, NgClass} from '@angular/common';
import {Toast} from 'primeng/toast';
import {Router} from '@angular/router';
import {Card} from 'primeng/card';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-email-settings-form',
  templateUrl: './email-settings-form.component.html',
  styleUrls: ['./email-settings-form.component.scss'],
  providers: [MessageService],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PasswordModule,
    ButtonModule,
    InputTextModule,
    NgClass,
    Toast,
    CommonModule,
    Card,
    TranslateModule
  ]
})
export class EmailSettingsFormComponent implements OnInit {
  readonly emailSettingsEnabled = false;
  emailForm!: FormGroup; // Added non-null assertion operator
  submitting = false;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private router: Router,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.initForm();

    if (!this.emailSettingsEnabled) {
      this.emailForm.disable({ emitEvent: false });
    }
  }

  private initForm(): void {
    this.emailForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]]
    });
  }

  onSubmit(): void {
    if (!this.emailSettingsEnabled) {
      this.messageService.add({
        severity: 'info',
        summary: 'Formulario deshabilitado',
        detail: 'La actualización de correo está deshabilitada temporalmente.'
      });
      return;
    }

    if (this.emailForm.invalid) {
      this.markFormGroupTouched(this.emailForm);
      return;
    }

    this.submitting = true;

    // Simulate API call
    setTimeout(() => {
      this.submitting = false;
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('settings.email.toast.success.summary'),
        detail: this.translate.instant('settings.email.toast.success.detail')
      });
    }, 1500);
  }

  onCancel(): void {
    if (!this.emailSettingsEnabled) {
      return;
    }

    this.emailForm.reset();
    this.router.navigate(['/layout']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  isFieldInvalid(field: string): boolean {
    const control = this.emailForm.get(field);
    return control ? (control.invalid && (control.dirty || control.touched)) : false;
  }

  getErrorMessage(field: string): string {
    const control = this.emailForm.get(field);

    if (!control) return '';

    if (control.hasError('required')) {
      return this.translate.instant('settings.email.errors.required');
    }

    if (field === 'email' && control.hasError('email')) {
      return this.translate.instant('settings.email.errors.invalidEmail');
    }

    return this.translate.instant('settings.email.errors.generic');
  }
}
